import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import clsx from "clsx";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { Pencil, Archive, ArchiveRestore, Merge, MoreHorizontal, Plus, Search, type LucideIcon } from "lucide-react";
import { archivePosition, Position, restorePosition } from "services/positions.service";
import PersonalasTabs from "./PersonalasTabs";
import PositionRoleChips from "./PositionRoleChips";
import PositionEditDialog from "./PositionEditDialog";
import PositionMergeDialog from "./PositionMergeDialog";
import { usePositionsCatalogue } from "./usePositionsCatalogue";
import { filterPositions, genderedTitle } from "./positions-helpers";
import { describePositionsError } from "./positions-errors";
import { useAppSelector } from "hooks";
import { canManagePositions } from "features/personnel/personnel-helpers";
import { isMaster } from "utils/onboardingPermissions";
import useIsPhone from "features/files/use-is-phone";

/**
 * Personalas → «Pareigos»: the company's positions catalogue (route
 * /app/:companyId/positions), under the Personalas tab strip. The same list
 * is the «Pareigos» tab of Tvarkos.
 */
export default function PositionsPage() {
  const { companyId } = useParams<{ companyId: string }>();
  const { t } = useTranslation();
  if (!companyId) return null;
  return (
    <div className="w-full px-6 py-4">
      <PersonalasTabs companyId={companyId} />
      <PositionsList companyId={companyId} heading={t("positions_title")} />
    </div>
  );
}

/**
 * The positions catalogue itself. Master | Access onboarding manage it; the WS
 * specialist only reads it. Archive instead of delete; «Sujungti» folds a
 * duplicate into another position. `heading` is left out where the screen
 * already names the list (the Tvarkos tab).
 */
export function PositionsList({ companyId, heading }: { companyId: string; heading?: string }) {
  const { t } = useTranslation();
  const role = useAppSelector((state) => state.authReducer.role);
  const canManage = canManagePositions(role);
  // On a phone the three row actions do not fit in a column: they sit in a «...» menu beside the title.
  const isPhone = useIsPhone();
  const catalogue = usePositionsCatalogue(companyId);
  const [query, setQuery] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState<Position | "new" | null>(null);
  const [merging, setMerging] = useState<Position | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const rows = useMemo(() => {
    const list = catalogue.positions
      .filter((position) => showArchived || !position.isArchived)
      .sort((a, b) =>
        a.isArchived === b.isArchived
          ? a.nameLT.localeCompare(b.nameLT, "lt")
          : a.isArchived
            ? 1
            : -1
      );
    return filterPositions(list, query);
  }, [catalogue.positions, showArchived, query]);

  const toggleArchived = async (position: Position) => {
    if (!position.isArchived) {
      const message =
        position.holdersCount > 0
          ? t("positions_archive_confirm_holders", { name: position.nameLT, count: position.holdersCount })
          : t("positions_archive_confirm", { name: position.nameLT });
      if (!window.confirm(message)) return;
    }
    setBusyId(position.id);
    try {
      if (position.isArchived) await restorePosition(companyId, position.id);
      else await archivePosition(companyId, position.id);
      catalogue.reload();
    } catch (e: any) {
      toast.error(describePositionsError(e, t));
    } finally {
      setBusyId(null);
    }
  };

  // Taller on a touch screen (any width), unchanged with a mouse.
  const actionClass =
    "inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 [@media(pointer:coarse)]:py-2.5";

  const rowActions = (position: Position) => [
    ...(position.isArchived
      ? []
      : [
          { key: "edit", label: t("edit"), icon: Pencil, run: () => setEditing(position) },
          { key: "merge", label: t("positions_merge"), icon: Merge, run: () => setMerging(position) },
        ]),
    {
      key: "archive",
      label: position.isArchived ? t("restore") : t("archive"),
      icon: position.isArchived ? ArchiveRestore : Archive,
      run: () => toggleArchived(position),
      disabled: busyId === position.id,
    },
  ];

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          {heading && <h1 className="text-2xl font-semibold">{heading}</h1>}
          <p className="mt-0.5 text-sm text-gray-500">{t("positions_page_hint")}</p>
        </div>
        {canManage && (
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 max-md:min-h-11"
            onClick={() => setEditing("new")}
          >
            <Plus className="h-4 w-4" aria-hidden />
            {t("positions_new_title")}
          </button>
        )}
      </div>

      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center gap-4 p-4">
          <label className="relative block w-full min-w-0 flex-1 sm:w-auto sm:min-w-[16rem]">
            <Search aria-hidden className="pointer-events-none absolute left-2 top-3 h-4 w-4 text-gray-400 md:top-2.5" />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t("positions_picker_search")}
              aria-label={t("positions_picker_search")}
              className="w-full rounded-lg border border-gray-300 py-2 pl-8 pr-3 text-base max-md:min-h-11 [@media(pointer:coarse)]:min-h-11 focus:outline-none focus:ring-2 focus:ring-blue-500 md:text-sm"
            />
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700 max-md:min-h-11">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(event) => setShowArchived(event.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 max-md:h-5 max-md:w-5"
            />
            {t("positions_show_archived")}
          </label>
        </div>

        {catalogue.loading && catalogue.positions.length === 0 ? (
          <p className="px-4 pb-4 text-sm text-gray-500">{t("loading")}</p>
        ) : catalogue.error ? (
          <div className="flex items-center gap-3 px-4 pb-4 text-sm text-red-700">
            {t("positions_load_failed")}
            <button type="button" className="underline" onClick={catalogue.reload}>
              {t("retry")}
            </button>
          </div>
        ) : rows.length === 0 ? (
          <p className="px-4 pb-4 text-sm text-gray-500">{t("positions_picker_empty")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2">{t("positions_name_lt")}</th>
                  <th className="hidden px-4 py-2 md:table-cell">{t("positions_name_en")}</th>
                  <th className="px-4 py-2 text-right">{t("positions_holders_title")}</th>
                  <th className="px-4 py-2">{t("positions_roles_title")}</th>
                  {canManage && !isPhone && <th className="px-4 py-2 text-right">{t("actions")}</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((position) => (
                  <tr key={position.id} className={clsx(position.isArchived && "bg-gray-50 text-gray-500")}>
                    <td className="px-4 py-2 align-top">
                      <div className="flex items-start justify-between gap-1">
                        <div>
                          {canManage ? (
                            <button
                              type="button"
                              className="text-left font-medium text-gray-900 hover:text-blue-700 max-md:py-2"
                              onClick={() => setEditing(position)}
                            >
                              {genderedTitle(position)}
                            </button>
                          ) : (
                            <span className="font-medium text-gray-900">{genderedTitle(position)}</span>
                          )}
                          {position.isArchived && (
                            <span className="ml-2 rounded bg-gray-200 px-1.5 py-0.5 text-xs text-gray-600">
                              {t("positions_archived_badge")}
                            </span>
                          )}
                          {isPhone && position.nameEN && <div className="text-xs text-gray-500">{position.nameEN}</div>}
                        </div>
                        {canManage && isPhone && (
                          <PositionActionsMenu label={t("more")} actions={rowActions(position)} />
                        )}
                      </div>
                    </td>
                    <td className="hidden px-4 py-2 align-top md:table-cell">{position.nameEN || "—"}</td>
                    <td className="px-4 py-2 text-right align-top tabular-nums">{position.holdersCount}</td>
                    <td className="px-4 py-2 align-top">
                      <PositionRoleChips roles={position.roles} />
                    </td>
                    {canManage && !isPhone && (
                      <td className="whitespace-nowrap px-4 py-2 text-right align-top">
                        {rowActions(position).map(({ key, label, icon: Icon, run, disabled }) => (
                          <button key={key} type="button" className={actionClass} onClick={run} disabled={disabled}>
                            <Icon className="h-3.5 w-3.5" aria-hidden />
                            {label}
                          </button>
                        ))}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {canManage && editing && (
        <PositionEditDialog
          companyId={companyId}
          position={editing === "new" ? null : editing}
          canEditPermissions={isMaster(role)}
          onClose={() => {
            setEditing(null);
            // A create may have gone through even if its pareigybės did not.
            if (editing === "new") catalogue.reload();
          }}
          onSaved={() => {
            setEditing(null);
            catalogue.reload();
          }}
        />
      )}
      {canManage && merging && (
        <PositionMergeDialog
          companyId={companyId}
          source={merging}
          positions={catalogue.active}
          onClose={() => setMerging(null)}
          onMerged={() => {
            setMerging(null);
            catalogue.reload();
          }}
        />
      )}
    </>
  );
}

type RowAction = {
  key: string;
  label: string;
  icon: LucideIcon;
  run: () => void;
  disabled?: boolean;
};

/** The «...» menu of one position row on a phone: edit, merge, archive. */
function PositionActionsMenu({ label, actions }: { label: string; actions: RowAction[] }) {
  return (
    <Menu>
      <MenuButton
        aria-label={label}
        className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100"
      >
        <MoreHorizontal className="h-5 w-5" aria-hidden />
      </MenuButton>
      <MenuItems
        anchor="bottom end"
        className="z-50 w-52 rounded-lg border border-gray-200 bg-white p-1 shadow-lg [--anchor-gap:4px] focus:outline-none"
      >
        {actions.map(({ key, label: actionLabel, icon: Icon, run, disabled }) => (
          <MenuItem key={key} disabled={disabled}>
            <button
              type="button"
              onClick={run}
              className="flex min-h-11 w-full items-center gap-2 rounded-md px-3 text-left text-sm text-gray-800 data-[focus]:bg-gray-100 data-[disabled]:opacity-50"
            >
              <Icon className="h-4 w-4" aria-hidden />
              {actionLabel}
            </button>
          </MenuItem>
        ))}
      </MenuItems>
    </Menu>
  );
}
